(() => {
  if (customElements.get('bfcm-sticky-banner')) return;

  class BfcmStickyBanner extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      clearTimeout(this.expiryTimer);
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.designMode = this.dataset.designMode === 'true';
      this.endAt = Date.parse(this.dataset.endAt || '');
      this.dismissDays = Math.max(0, Number(this.dataset.dismissDays) || 0);
      this.storageKey = `ebs-bfcm-dismissed:${this.dataset.campaign}:${this.id}`;
      this.shown = false;
      this.dismissed = false;
      this.hidden = true;
      this.classList.remove('is-visible');

      if (!this.designMode) {
        try {
          const dismissedAt = Number(localStorage.getItem(this.storageKey));
          this.dismissed = dismissedAt > 0 && Date.now() - dismissedAt < this.dismissDays * 86400000;
        } catch { /* The banner still works when storage is unavailable. */ }
      }

      const check = () => this.checkVisibility();
      window.addEventListener('scroll', check, { passive: true, signal });
      window.addEventListener('resize', check, { passive: true, signal });
      document.addEventListener('visibilitychange', check, { signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (this.designMode && event.target.contains(this)) {
          this.dismissed = false;
          this.checkVisibility();
        }
      }, { signal });

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-ebs-cta]')) this.track('BFCM Sticky Clicked');
        if (!event.target.closest('[data-ebs-close]')) return;
        this.dismissed = true;
        this.hidden = true;
        this.classList.remove('is-visible');
        if (!this.designMode) {
          try {
            localStorage.setItem(this.storageKey, String(Date.now()));
          } catch { /* Closing does not require persistent storage. */ }
        }
        this.track('BFCM Sticky Dismissed');
      }, { signal });

      this.checkVisibility();
      this.scheduleExpiry();
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.expiryTimer);
    }

    checkVisibility() {
      if (this.dismissed || (!this.designMode && Number.isFinite(this.endAt) && Date.now() >= this.endAt)) {
        this.hidden = true;
        this.classList.remove('is-visible');
        return;
      }
      const page = document.documentElement;
      const maxScroll = page.scrollHeight - page.clientHeight;
      const progress = maxScroll > 0 ? (window.scrollY || page.scrollTop) / maxScroll : 1;
      const threshold = Math.min(100, Math.max(0, Number(this.dataset.scroll) || 0)) / 100;
      if (this.designMode || this.shown || progress >= threshold) {
        this.hidden = false;
        this.classList.add('is-visible');
        if (!this.shown) {
          this.shown = true;
          this.track('BFCM Sticky Viewed');
        }
      }
    }

    scheduleExpiry() {
      if (this.designMode || !Number.isFinite(this.endAt) || Date.now() >= this.endAt) return;
      // Long campaigns must stay within the browser's maximum timeout duration.
      this.expiryTimer = setTimeout(() => {
        this.checkVisibility();
        this.scheduleExpiry();
      }, Math.min(this.endAt - Date.now(), 2147483647));
    }

    track(name) {
      if (this.designMode) return;
      try {
        window.mixpanel?.track(name, { page: location.pathname });
      } catch { /* Analytics must not interrupt storefront interaction. */ }
    }
  }

  customElements.define('bfcm-sticky-banner', BfcmStickyBanner);
})();
